/**
 * Dinero como entero exacto (bigint) en unidades minimas, mas moneda y escala.
 *
 * NFR-MONEY-1: prohibido el punto flotante binario para dinero en cualquier
 * capa. bigint hace que esta prohibicion sea estructural, no una convencion:
 * no existe operacion de coma flotante disponible sobre este tipo.
 */

export type CurrencyCode = string; // ISO-4217, p.ej. "EUR", "USD"

export interface Money {
  readonly amountMinor: bigint;
  readonly currency: CurrencyCode;
  readonly scale: number;
}

export class CurrencyMismatchError extends Error {
  constructor(a: CurrencyCode, b: CurrencyCode) {
    super(`No se puede operar entre monedas distintas: ${a} y ${b}`);
    this.name = "CurrencyMismatchError";
  }
}

export class EmptyMoneyListError extends Error {
  constructor() {
    super("sumMoney requiere al menos un elemento");
    this.name = "EmptyMoneyListError";
  }
}

function assertValidCurrency(currency: string): void {
  if (!/^[A-Z]{3}$/.test(currency)) {
    throw new RangeError(`Codigo de moneda invalido (ISO-4217 de 3 letras esperado): ${currency}`);
  }
}

function assertValidScale(scale: number): void {
  if (!Number.isInteger(scale) || scale < 0 || scale > 18) {
    throw new RangeError(`Escala invalida: ${scale}`);
  }
}

export function money(amountMinor: bigint | number, currency: CurrencyCode, scale: number): Money {
  assertValidCurrency(currency);
  assertValidScale(scale);
  const amount = typeof amountMinor === "bigint" ? amountMinor : BigInt(Math.trunc(amountMinor));
  return { amountMinor: amount, currency, scale };
}

function assertSameDenomination(a: Money, b: Money): void {
  if (a.currency !== b.currency) {
    throw new CurrencyMismatchError(a.currency, b.currency);
  }
  if (a.scale !== b.scale) {
    throw new RangeError(
      `Escala inconsistente para ${a.currency}: ${a.scale} vs ${b.scale}. La escala de una moneda es fija.`,
    );
  }
}

export function addMoney(a: Money, b: Money): Money {
  assertSameDenomination(a, b);
  return { amountMinor: a.amountMinor + b.amountMinor, currency: a.currency, scale: a.scale };
}

export function negateMoney(a: Money): Money {
  return { amountMinor: -a.amountMinor, currency: a.currency, scale: a.scale };
}

export function isZeroMoney(a: Money): boolean {
  return a.amountMinor === 0n;
}

export function equalMoney(a: Money, b: Money): boolean {
  return a.currency === b.currency && a.scale === b.scale && a.amountMinor === b.amountMinor;
}

/** Suma una lista de Money agrupando por (currency, scale). Nunca mezcla monedas en el resultado. */
export function sumByCurrency(items: readonly Money[]): Map<CurrencyCode, Money> {
  const totals = new Map<CurrencyCode, Money>();
  for (const item of items) {
    const existing = totals.get(item.currency);
    totals.set(item.currency, existing ? addMoney(existing, item) : item);
  }
  return totals;
}

export function sumMoney(items: readonly Money[]): Money {
  if (items.length === 0) throw new EmptyMoneyListError();
  const [first, ...rest] = items as [Money, ...Money[]];
  return rest.reduce((acc, m) => addMoney(acc, m), first);
}

/**
 * Representacion decimal exacta para presentacion (nunca para calculo).
 * Ej.: money(1234n, "EUR", 2) -> "12.34"
 */
export function formatDecimal(m: Money): string {
  const negative = m.amountMinor < 0n;
  const abs = negative ? -m.amountMinor : m.amountMinor;
  const s = abs.toString().padStart(m.scale + 1, "0");
  const cut = s.length - m.scale;
  const intPart = s.slice(0, cut);
  const fracPart = m.scale > 0 ? "." + s.slice(cut) : "";
  return (negative ? "-" : "") + intPart + fracPart;
}
